// src/hooks/useAuthHeaders.ts
//
// Shared hook to get authentication headers for API requests.
// Supports Auth0, EntraID, and unsafe (development) modes.

import { useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMsal } from "@azure/msal-react";
import { getBrowserTimezone } from "../utils/userTimezone";
import {
  getAuthenticationMethod,
  isMockApiRuntime,
} from "../config/runtimeConfig";

export const useAuthHeaders = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { instance, accounts } = useMsal();

  return useCallback(async (): Promise<Record<string, string>> => {
    const timezone = getBrowserTimezone();

    // Check authentication method
    if (isMockApiRuntime()) {
      return { "X-User-Timezone": timezone };
    }

    const authenticationMethod = getAuthenticationMethod();
    if (authenticationMethod === "AUTH0") {
      const token = await getAccessTokenSilently();
      return {
        Authorization: `Bearer ${token}`,
        "X-User-Timezone": timezone,
      };
    }

    if (authenticationMethod === "ENTRAID") {
      const request = {
        scopes: [
          "api://0501f640-94a0-4fd9-a7ae-0e8fa5938130/access_as_user",
          "email",
          "openid",
          "profile",
        ],
        account: accounts[0],
      };
      await instance.initialize();
      const response = await instance.acquireTokenSilent(request);
      return {
        Authorization: `Bearer ${response.accessToken}`,
        "X-User-Timezone": timezone,
      };
    }

    return { "X-User-Timezone": timezone };
  }, [getAccessTokenSilently, instance, accounts]);
};
